<template>
	<div v-auto-animate>
		<BaseAlert
			type="error"
			v-if="error"
		>
			{{ error }}
		</BaseAlert>
		<FormKit
			type="form"
			@submit="onSubmit"
			:actions="false"
			#default="{ disabled }"
			form-class="mt-8"
			id="loginForm"
		>
			<FormKit
				type="email"
				name="email"
				:label="t('auth.email')"
				validation="required|email"
				v-model="email"
				outer-class="max-w-full"
				autofocus
				@keydown.tab="autoCompleteEmail"
			/>

			<FormKit
				type="password"
				name="password"
				:label="t('auth.password')"
				validation="required"
				outer-class="max-w-full"
				suffix-icon="eyeClosed"
				@suffix-icon-click="handleIconClick"
				v-model="password"
			/>

			<!-- Avoid prettier warning unwrapping type coercion, which breaks syntax highlight -->
			<!-- prettier-ignore-attribute -->
			<FormKit
				type="submit"
				outer-class="max-w-full mt-9"
				input-class="w-full justify-center"
				:disabled="(disabled as boolean)"
				>{{ t('auth.login') }}</FormKit
			>
		</FormKit>
		<a
			:href="resetPasswordUrl"
			class="text-center text-sm text-red-400 hover:underline"
			>{{ t('auth.forgotPassword') }}</a
		>
	</div>
</template>

<script setup lang="ts">
import BaseAlert from '@/components/common/BaseAlert.vue'
import { useAuthStore } from '@/stores/auth'
import { useSyncStore } from '@/stores/sync'
import { type FormKitNode } from '@formkit/core'
import { FormKit } from '@formkit/vue'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const auth = useAuthStore()
const email = ref('')
const password = ref('')
const resetPasswordUrl = import.meta.env.VITE_API_ROOT + '/reset'
const error = ref('')
const sync = useSyncStore()

const handleIconClick = (node: FormKitNode) => {
	node.props.suffixIcon = node.props.suffixIcon === 'eye' ? 'eyeClosed' : 'eye'
	node.props.type = node.props.type === 'password' ? 'text' : 'password'
}

/** If the user finishes typing an email address w/o a domain, add @precoa.com */
function autoCompleteEmail() {
	if (
		email.value.length >= 4 &&
		!email.value.includes('@') &&
		!email.value.includes('.')
	) {
		email.value += '@precoa.com'
	}
}

/** Try to log in with email and password */
const onSubmit = () => {
	error.value = ''
	return auth
		.getUserFromServer({
			username: email.value,
			password: password.value,
		})
		.then(async () => {
			await auth.getStoryBlokToken()
			auth.showLogin = false

			if (auth.user?.salesContact) {
				sync.beginSync()
			}
		})
		.catch((e) => {
			error.value = e.message
			console.error(e)
		})
}
</script>
